import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { LinearProgress, Title, usePermissions } from "react-admin";
import { Box, Typography } from "@mui/material";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import BackButtonToolbar from "features/BackButtonToolbar";
import SemesterAccordion from "./SemesterAccordion";
import { groupDisciplines } from "./utils";

const ManageAttestationsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const notifyError = useNotifyError();
  const { permissions: userPermissions, isPending: permissionsPending } = usePermissions();

  const canManage = checkPermission(userPermissions, permissions.canManageStudentEducationAttestations);

  const { facultyNumber, studentName, curriculumName, course } = location?.state ?? {};

  const [data, setData] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const fetchAttestations = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationAttestationsManagement(id),
        null,
        "GET",
      );
      setData(data ?? null);
    } catch (err) {
      notifyError(err);
    }
  }, [id, notifyError]);

  useEffect(() => {
    if (!id) return;
    fetchAttestations();
  }, [id, fetchAttestations]);

  // Memoized so the semester forms are only re-seeded when fresh data actually arrives.
  const groups = useMemo(() => groupDisciplines(data?.disciplines), [data]);

  // Only one group is open at a time: saving refetches the whole education, which re-seeds every
  // mounted form - with a single accordion open that can never discard edits in another group.
  // It opens on the student's current course, where the work usually is: the groups are sorted by
  // course, so the first match is its winter semester (or the course itself when it has no semesters).
  useEffect(() => {
    setExpandedGroup(previous => {
      const currentCourseGroup = groups.find(group => group.course === course);
      return previous ?? currentCourseGroup?.key ?? groups[0]?.key ?? null;
    });
  }, [groups, course]);

  return (
    <Box sx={{ width: "100%", overflow: "hidden", pb: 3 }}>
      <Title title="Управление на заверки" />
      <BackButtonToolbar />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: 3 }}>
        {studentName && (
          <Typography>
            Студент: <strong>{studentName}</strong>
          </Typography>
        )}
        {facultyNumber && (
          <Typography>
            Факултетен номер: <strong>{facultyNumber}</strong>
          </Typography>
        )}
        {curriculumName && (
          <Typography>
            Учебен план: <strong>{curriculumName}</strong>
          </Typography>
        )}
      </Box>
      {/* Permissions decide whether the rows are inputs or plain text, so nothing is shown until they load. */}
      {permissionsPending ? (
        <LinearProgress />
      ) : (
        <>
          {data && !groups.length && <Typography color="secondary">Няма намерени дисциплини.</Typography>}
          {groups.map(group => (
            <SemesterAccordion
              key={group.key}
              studentEducationId={id}
              group={group}
              canManage={canManage}
              expanded={expandedGroup === group.key}
              onToggle={() => setExpandedGroup(previous => (previous === group.key ? null : group.key))}
              onSaved={fetchAttestations}
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default ManageAttestationsPage;
